import React from "react";
import { 
  FiEye, 
  FiEdit2, 
  FiCheckCircle, 
  FiXCircle, 
  FiClock,
  FiTag,
  FiDollarSign,
  FiCalendar,
  FiUser,
  FiTrendingUp,
  FiAward,
  FiStar,
  FiHash,
  FiRefreshCw,
  FiFilter,
  FiSearch
} from "react-icons/fi";
import { 
  MdOutlineVisibility, 
  MdOutlineEdit,
  MdOutlineCheckCircle,
  MdOutlineCancel,
  MdOutlinePending
} from "react-icons/md";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { FaEdit } from "react-icons/fa";
import { IoCloseCircleOutline } from "react-icons/io5";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import ReactPaginate from "react-paginate";
import SortableHeader from "../../../components/SortableHeader";

const PromotationTable = ({
  TableName,
  TableHeader,
  handlePageClick,
  TableData,
  filter,
  viewAction,
  handleStatus,
  total,
  openConfirmModalForPromotions,
  currentSortKey,
  currentSortOrder,
  onSort
}) => {
  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      'approved': { 
        icon: FiCheckCircle, 
        color: 'text-emerald-700 bg-emerald-50 border-emerald-200', 
        label: 'Approved' 
      },
      'active': { 
        icon: FiCheckCircle, 
        color: 'text-emerald-700 bg-emerald-50 border-emerald-200', 
        label: 'Active' 
      },
      'pending': { 
        icon: FiClock, 
        color: 'text-amber-700 bg-amber-50 border-amber-200', 
        label: 'Pending' 
      },
      'rejected': { 
        icon: FiXCircle, 
        color: 'text-rose-700 bg-rose-50 border-rose-200', 
        label: 'Rejected' 
      },
      'expired': { 
        icon: FiClock, 
        color: 'text-gray-700 bg-gray-50 border-gray-200', 
        label: 'Expired' 
      }
    };
    const config = statusConfig[status?.toLowerCase()] || statusConfig['pending'];
    const Icon = config.icon;
    
    return (
      <span className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
        ${config.color}
      `}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100/80 overflow-hidden">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 md:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-lg">
              <FiTrendingUp className="w-4 h-4 text-[#715EFE]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">{TableName}</h2>
              <p className="text-xs text-gray-400 hidden sm:block">
                {TableData?.length || 0} boost promotions
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-full border border-gray-200">
              <FiClock className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-500">
                {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50/80 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                {TableHeader?.map((heading, index) => {
                  const headerIcons = {
                    '#': null,
                    'Title': FiTag,
                    'Status': FiClock,
                    'Usage Limit': FiHash,
                    'Charges': FiDollarSign,
                    'Action': null
                  };
                  const Icon = headerIcons[heading.label] || headerIcons[heading.label?.trim()];
                  return (
                    <td key={index} className="px-3 md:px-4 py-3 whitespace-nowrap">
                      <SortableHeader
                        label={heading.label}
                        sortKey={heading.sortKey}
                        currentSortKey={currentSortKey}
                        currentSortOrder={currentSortOrder}
                        onSort={onSort}
                        icon={Icon}
                      />
                    </td>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {TableData && TableData.length > 0 ? (
                TableData?.map((learner, index) => {
                  const isPending = learner?.status === 'pending';
                  
                  return (
                    <tr key={index} className="hover:bg-gray-50/70 transition-colors duration-150 group">
                      <td className="px-3 md:px-4 py-3">
                        <span className="text-sm font-medium text-gray-400">
                          {(filter.pageNo - 1) * filter.size + index + 1}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center">
                            <FiTag className="w-3.5 h-3.5 text-[#715EFE]" />
                          </div>
                          <span className="text-sm font-medium text-gray-800 truncate max-w-[150px]">
                            {learner.courseId?.title?.length > 25 
                              ? learner.courseId.title.slice(0, 25) + "..." 
                              : learner.courseId.title}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <div className="flex flex-col items-start gap-1.5">
                          <StatusBadge status={learner?.status} />
                          {isPending && (
                            <div className="flex items-center gap-1">
                              <button
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-lg transition-all duration-200"
                                onClick={() => handleStatus("rejected", learner)}
                                title="Reject"
                              >
                                <FiXCircle className="w-3 h-3" />
                                Reject
                              </button>
                              <button
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-all duration-200"
                                onClick={() => handleStatus("approved", learner)}
                                title="Approve"
                              >
                                <FiCheckCircle className="w-3 h-3" />
                                Approve
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <span className="text-sm text-gray-700">
                          {learner?.planId?.durationInDays || 0} Day{learner?.planId?.durationInDays !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <span className="text-sm font-semibold text-emerald-600">
                          ${learner?.planId?.price || 0}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                            onClick={() => viewAction(learner)}
                            title="View Details"
                          >
                            <FiEye className="w-4 h-4" />
                          </button>
                          <button
                            className="p-1.5 text-[#715EFE] hover:text-[#855DF6] hover:bg-[#715EFE]/10 rounded-lg transition-all duration-200"
                            onClick={() => openConfirmModalForPromotions({
                              _id: learner._id,
                              type: "boost",
                              status: learner.status,
                            })}
                            title="Update Status"
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={TableHeader?.length || 6} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-4 bg-gray-50 rounded-full">
                        <FiTrendingUp className="w-10 h-10 text-gray-300" />
                      </div>
                      <p className="text-sm font-medium text-gray-500">No boost promotions found</p>
                      <p className="text-xs text-gray-400">Boost promotions will appear here</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Section */}
        {total > filter.size && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
            <div className="text-sm text-gray-500">
              Showing{" "}
              <span className="font-medium">
                {Math.min((filter.pageNo - 1) * filter.size + 1, total)}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(filter.pageNo * filter.size, total)}
              </span>{" "}
              of <span className="font-medium">{total}</span> results
            </div>
            <ReactPaginate
              forcePage={filter.pageNo - 1}
              previousLabel={
                <span className="flex items-center gap-1">
                  <span>Prev</span>
                </span>
              }
              nextLabel={
                <span className="flex items-center gap-1">
                  <span>Next</span>
                </span>
              }
              breakLabel={"..."}
              breakClassName={"px-2 text-gray-400"}
              pageCount={Math.ceil(total / filter.size)}
              marginPagesDisplayed={1}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"flex items-center gap-1"}
              pageClassName={"px-2"}
              pageLinkClassName={"px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200"}
              previousClassName={"px-2"}
              previousLinkClassName={"px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200"}
              nextClassName={"px-2"}
              nextLinkClassName={"px-3 py-1 text-sm text-gray-600 rounded-md hover:bg-[#715EFE]/10 hover:text-[#715EFE] transition-colors duration-200"}
              activeClassName={"bg-[#715EFE] text-white rounded-md"}
              activeLinkClassName={"bg-[#715EFE] text-white px-3 py-1 rounded-md"}
              disabledClassName={"opacity-50 cursor-not-allowed"}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotationTable;